import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import AppLayout from "@/layouts/AppLayout"
import { DndContext, type DragEndEvent, PointerSensor, closestCorners, useSensor, useSensors } from "@dnd-kit/core"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { IconArrowLeft, IconListDetails, IconPlus, IconUserPlus } from "@tabler/icons-react"
import { useEffect, useState } from "react"
import { BoardColumn } from "./components/BoardColumn"
import { BoardNavigator } from "./components/BoardNavigator"
import { BoardShareDialog } from "./components/BoardShareDialog"
import { ColumnDialog } from "./components/ColumnDialog"
import { FieldsDialog } from "./components/FieldsDialog"
import { NewTicketDialog } from "./components/NewTicketDialog"
import type { BoardDetail, BoardFieldDefinition, Column, Navigation, Person } from "./types"

interface Props {
  [key: string]: unknown
  board: BoardDetail
  columns: Column[]
  assignable_users: Person[]
  shareable_users: { id: number; name: string; email: string }[]
  navigation: Navigation
  fields: BoardFieldDefinition[]
}

export default function BoardsShow() {
  const {
    board,
    columns: serverColumns,
    assignable_users,
    shareable_users,
    navigation,
    fields,
  } = usePage<Props>().props

  const [columns, setColumns] = useState(serverColumns)
  const [ticketColumn, setTicketColumn] = useState<Column | null>(null)
  const [editingColumn, setEditingColumn] = useState<Column | null>(null)
  const [addingColumn, setAddingColumn] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)
  const [fieldsOpen, setFieldsOpen] = useState(false)

  useEffect(() => setColumns(serverColumns), [serverColumns])

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

  const columnOf = (ticketKey: string) => columns.find((column) => column.tickets.some((t) => t.key === ticketKey))

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over) return

    const ticketKey = String(active.id)
    const from = columnOf(ticketKey)
    if (!from) return

    const overId = String(over.id)
    const to = columns.find((column) => `column-${column.id}` === overId) ?? columnOf(overId)
    if (!to) return

    const ticket = from.tickets.find((t) => t.key === ticketKey)
    if (!ticket) return

    const overIndex = to.tickets.findIndex((t) => t.key === overId)
    const remaining = to.id === from.id ? to.tickets.filter((t) => t.key !== ticketKey) : to.tickets
    const index = overIndex >= 0 ? overIndex : remaining.length

    if (to.id === from.id && from.tickets[index]?.key === ticketKey) return

    const next = remaining.slice()
    next.splice(index, 0, ticket)

    setColumns(
      columns.map((column) => {
        if (column.id === to.id) return { ...column, tickets: next }
        if (column.id === from.id) return { ...column, tickets: column.tickets.filter((t) => t.key !== ticketKey) }
        return column
      }),
    )

    router.patch(
      `/tickets/${ticketKey}/move`,
      { board_column_id: to.id, position: index },
      { preserveScroll: true, preserveState: true, only: ["columns"] },
    )
  }

  return (
    <>
      <Head title={board.name} />
      <div className="flex flex-1">
        <BoardNavigator navigation={navigation} />

        <main className="flex min-w-0 flex-1 flex-col px-8 py-10">
          <header className="mb-6 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <Link
                href="/boards"
                className="text-muted-foreground mb-1 inline-flex items-center gap-1 text-xs hover:underline"
              >
                <IconArrowLeft className="size-3" />
                All boards
              </Link>
              <div className="flex items-center gap-2">
                <h1 className="truncate text-2xl font-semibold">{board.name}</h1>
                <Badge variant="outline" className="font-mono">
                  {board.key}
                </Badge>
                {board.visibility === "company" && <Badge variant="secondary">Company</Badge>}
              </div>
              {board.project && <p className="text-muted-foreground mt-1 text-sm">{board.project.name}</p>}
              {board.description && <p className="text-muted-foreground mt-1 text-sm">{board.description}</p>}
            </div>

            {board.can_administer && (
              <div className="flex shrink-0 items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setFieldsOpen(true)}>
                  <IconListDetails className="size-4" />
                  Fields
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShareOpen(true)}>
                  <IconUserPlus className="size-4" />
                  Share
                </Button>
              </div>
            )}
          </header>

          <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={onDragEnd}>
            <div className="flex flex-1 items-start gap-4 overflow-x-auto pb-4">
              {columns.map((column) => (
                <BoardColumn
                  key={column.id}
                  column={column}
                  canEdit={board.can_edit}
                  canAdminister={board.can_administer}
                  onAddTicket={setTicketColumn}
                  onEditColumn={setEditingColumn}
                />
              ))}

              {board.can_administer && (
                <Button variant="ghost" className="mt-7 shrink-0" onClick={() => setAddingColumn(true)}>
                  <IconPlus className="size-4" />
                  Add column
                </Button>
              )}
            </div>
          </DndContext>
        </main>
      </div>
      {ticketColumn && (
        <NewTicketDialog
          boardKey={board.key}
          column={ticketColumn}
          assignableUsers={assignable_users}
          onOpenChange={(open) => !open && setTicketColumn(null)}
        />
      )}

      {(editingColumn || addingColumn) && (
        <ColumnDialog
          boardKey={board.key}
          column={editingColumn}
          onOpenChange={(open) => {
            if (!open) {
              setEditingColumn(null)
              setAddingColumn(false)
            }
          }}
        />
      )}

      {board.can_administer && (
        <FieldsDialog open={fieldsOpen} onOpenChange={setFieldsOpen} boardKey={board.key} fields={fields} />
      )}

      {board.can_administer && (
        <BoardShareDialog open={shareOpen} onOpenChange={setShareOpen} board={board} shareableUsers={shareable_users} />
      )}
    </>
  )
}

BoardsShow.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
